import React, { createContext, lazy, Suspense, useCallback, useContext, useMemo, useRef, useState } from 'react';
import blobStorageService from '../services/blobStorage';
import {
  getPreviewKindFromFilename,
  refinePreviewKindFromMime,
  resolveAttachmentDisplayName,
  PreviewKind
} from '../utils/attachmentPreview';

/** Lazy-loaded so this module finishes init before heavy preview deps; avoids circular-import TDZ on `AttachmentViewerContext`. */
const AttachmentViewerModal = lazy(() => import('../components/AttachmentViewerModal'));

const AttachmentViewerContext = createContext(null);

function basenameDisplay(name) {
  if (name == null || name === '') return 'download';
  const s = String(name);
  return s.includes('/') ? s.split('/').pop() : s.includes('\\') ? s.split('\\').pop() : s;
}

export function AttachmentViewerProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [previewKind, setPreviewKind] = useState(null);
  const [filename, setFilename] = useState('');
  const [error, setError] = useState(null);
  const objectUrlRef = useRef(null);

  const revokeObjectUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setObjectUrl(null);
  }, []);

  const closeViewer = useCallback(() => {
    revokeObjectUrl();
    setPreviewBlob(null);
    setPreviewKind(null);
    setOpen(false);
    setLoading(false);
    setFilename('');
    setError(null);
  }, [revokeObjectUrl]);

  /**
   * Opens the modal after a Blob is available. Creates an object URL only for PDF and image previews.
   */
  const openPreviewFromBlob = useCallback(
    (blob, displayName) => {
      const name = basenameDisplay(displayName);
      let kind = getPreviewKindFromFilename(name);
      kind = refinePreviewKindFromMime(kind, blob.type);

      setError(null);
      setFilename(name);
      setPreviewBlob(blob);
      setPreviewKind(kind);
      setOpen(true);
      setLoading(false);

      revokeObjectUrl();
      if (kind === PreviewKind.PDF || kind === PreviewKind.IMAGE) {
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setObjectUrl(url);
      } else {
        setObjectUrl(null);
      }
    },
    [revokeObjectUrl]
  );

  const openFromContainer = useCallback(
    async (blobName, containerName, displayName) => {
      const name = resolveAttachmentDisplayName(displayName, blobName);
      setError(null);
      setFilename(name);
      setOpen(true);
      setLoading(true);
      revokeObjectUrl();
      setPreviewBlob(null);
      setPreviewKind(null);
      setObjectUrl(null);
      try {
        const blob = await blobStorageService.fetchBlobForViewFromContainer(blobName, containerName);
        openPreviewFromBlob(blob, name);
        return 'viewer';
      } catch (e) {
        setError(e?.message || 'Failed to load file');
        setPreviewBlob(null);
        setPreviewKind(null);
        setOpen(true);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [openPreviewFromBlob, revokeObjectUrl]
  );

  const openFromDefaultBlobStorage = useCallback(
    async (blobName, displayName) => {
      const name = basenameDisplay(displayName || blobName);
      setError(null);
      setFilename(name);
      setOpen(true);
      setLoading(true);
      revokeObjectUrl();
      setPreviewBlob(null);
      setPreviewKind(null);
      setObjectUrl(null);
      try {
        const blob = await blobStorageService.downloadFile(blobName);
        openPreviewFromBlob(blob, name);
        return 'viewer';
      } catch (e) {
        setError(e?.message || 'Failed to load file');
        setPreviewBlob(null);
        setPreviewKind(null);
        setOpen(true);
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [openPreviewFromBlob, revokeObjectUrl]
  );

  /**
   * PDF or other blob from generate/export endpoints (already a PDF or any blob).
   */
  const openPdfBlob = useCallback(
    (blob, displayName) => {
      setError(null);
      openPreviewFromBlob(blob, displayName);
      return 'viewer';
    },
    [openPreviewFromBlob]
  );

  const value = useMemo(
    () => ({
      openFromContainer,
      openFromDefaultBlobStorage,
      openPdfBlob,
      closeViewer
    }),
    [openFromContainer, openFromDefaultBlobStorage, openPdfBlob, closeViewer]
  );

  return (
    <AttachmentViewerContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        <AttachmentViewerModal
          open={open}
          onClose={closeViewer}
          blob={previewBlob}
          objectUrl={objectUrl}
          filename={filename}
          previewKind={previewKind}
          loading={loading}
          error={error}
        />
      </Suspense>
    </AttachmentViewerContext.Provider>
  );
}

export function useAttachmentViewer() {
  const ctx = useContext(AttachmentViewerContext);
  if (!ctx) {
    throw new Error('useAttachmentViewer must be used within AttachmentViewerProvider');
  }
  return ctx;
}
